import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, MessageCircle, Mail, MessageSquare, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { GridBackground } from '@/components/GridBackground';
import { MainNavigation } from '@/components/MainNavigation';
import { Footer } from '@/components/Footer';

const supportChannels = [
  {
    icon: MessageCircle,
    title: 'WhatsApp Support',
    description: 'Get instant help through our WhatsApp chat. Available 24/7 for urgent issues.',
    action: 'Open WhatsApp',
    link: 'https://wa.me/1234567890'
  },
  {
    icon: Mail,
    title: 'Email Support',
    description: 'Send us a detailed message and we\'ll respond within 24 hours.',
    action: 'Email Us',
    link: 'mailto:support@xtreamcube.com'
  },
  {
    icon: MessageSquare,
    title: 'Live Chat',
    description: 'Use the chat widget in the bottom-right corner for instant assistance.',
    action: 'Find Widget ↘️',
    link: '#'
  }
];

export const SupportPage = () => {
  return (
    <div className="min-h-screen relative">
      <GridBackground />
      <MainNavigation />

      <main className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <Button asChild variant="outline" className="mb-8 glass border-primary/30">
            <Link to="/">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Link>
          </Button>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="glass rounded-2xl p-8 md:p-12 space-y-8"
          >
            <div className="text-center space-y-4">
              <h1 className="text-4xl font-bold">Support Center</h1>
              <p className="text-muted-foreground">
                We're here to help! Choose your preferred way to reach us.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {supportChannels.map((channel, index) => (
                <motion.div
                  key={channel.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="space-y-4 p-6 rounded-lg bg-card/50 border border-border text-center"
                >
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mx-auto">
                    <channel.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold">{channel.title}</h3>
                  <p className="text-sm text-muted-foreground">
                    {channel.description}
                  </p>

                  {channel.title === 'Live Chat' ? (
                    <Button
                      variant="outline"
                      className="w-full border-primary/30 hover:bg-primary hover:text-white transition-all duration-300"
                      onClick={(e) => {
                        e.preventDefault();


                        // Method 1: Official API (if available)
                        if (window.chatway && typeof window.chatway.open === 'function') {

                          window.chatway.open();
                          return;
                        }

                        // Method 2: Click the launcher element (try multiple selectors)
                        const launcher = document.querySelector('.chatway-launcher') ||
                          document.getElementById('chatway-launcher') ||
                          document.querySelector('[id^="chatway-launcher"]');
                        if (launcher) {

                          launcher.click();
                          return;
                        }

                        // Method 3: Find iframe and click parent
                        const iframe = document.querySelector('iframe[title="Chatway"]') ||
                          document.querySelector('iframe[src*="chatway.app"]');
                        if (iframe) {

                          // Navigate up to find the container that might handle clicks
                          const container = iframe.closest('div') || iframe.parentElement;
                          if (container) container.click();
                        } else {
                          console.warn('Chatway widget not found.');
                          // Visual fallback
                          const widgetArea = document.createElement('div');
                          Object.assign(widgetArea.style, {
                            position: 'fixed',
                            bottom: '20px',
                            right: '20px',
                            width: '60px',
                            height: '60px',
                            borderRadius: '50%',
                            border: '4px solid #22c55e',
                            zIndex: '9999',
                            pointerEvents: 'none',
                            transition: 'all 0.5s ease'
                          });
                          document.body.appendChild(widgetArea);
                          // Animate
                          requestAnimationFrame(() => {
                            widgetArea.style.transform = 'scale(1.2)';
                            widgetArea.style.opacity = '0';
                          });
                          setTimeout(() => widgetArea.remove(), 1000);
                        }
                      }}
                    >
                      {channel.action}
                    </Button>
                  ) : (
                    <Button
                      asChild
                      variant="outline"
                      className="w-full border-primary/30 hover:bg-primary hover:text-white transition-all duration-300"
                    >
                      <a href={channel.link} target="_blank" rel="noopener noreferrer">
                        {channel.action}
                      </a>
                    </Button>
                  )}
                </motion.div>
              ))}
            </div>

            <div className="pt-6 border-t border-border space-y-4">
              <div className="flex items-start gap-3">
                <Clock className="w-6 h-6 text-primary mt-1" />
                <div>
                  <h3 className="text-lg font-semibold mb-2">Support Hours</h3>
                  <ul className="space-y-1 text-muted-foreground text-sm">
                    <li>• WhatsApp: 24/7 (Automated + Live Agent)</li>
                    <li>• Email: Responses within 24 hours</li>
                    <li>• Live Chat: Available 24/7</li>
                  </ul>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </main>
      <Footer />
    </div>
  );
};